import Dialog from "@mui/material/Dialog";
import { useState, useEffect, useCallback, useRef } from "react";
import { FaMapMarkerAlt, FaTimes } from "react-icons/fa";
import { toast } from "sonner";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { createPortal } from "react-dom";

const Maps_API_KEY = "AIzaSyCRQXge0eI79YVPiihiUrqOKv4NapMnA9I";

const LocationSelector = ({
  initialLocation = null,
  onLocationSelected,
  showPickerInitially = false,
  displayClassName = "",
  displayOnly = false,
}) => {
  // State management
  const [currentLocation, setCurrentLocation] = useState(() => {
    return (
      initialLocation || {
        city: "",
        state: "",
        latitude: null,
        longitude: null,
        country: "",
        area: "",
        formattedAddress: "",
      }
    );
  });
  const [showPicker, setShowPicker] = useState(showPickerInitially);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Refs
  const mapsScriptRef = useRef(null);

  // Extract location details from address components
  const extractLocationDetails = (addressComponents) => {
    let city = "";
    let state = "";
    let country = "";
    let area = "";
    let postalCode = "";

    addressComponents.forEach((component) => {
      const types = component.types;
      if (types.includes("locality")) {
        city = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        state = component.long_name;
      } else if (types.includes("country")) {
        country = component.long_name;
      } else if (types.includes("postal_code")) {
        postalCode = component.long_name;
      }
      const areaTypes = [
        "neighborhood",
        "sublocality_level_1",
        "sublocality",
        "postal_town",
        "route",
        "political",
      ];

      if (!area) {
        for (const type of areaTypes) {
          if (types.includes(type)) {
            area = component.long_name;
            break;
          }
        }
      }
    });

    return { city, state, country, area, postalCode };
  };

  // Get address from coordinates
  const getAddressFromCoordinates = useCallback(async (latitude, longitude) => {
    if (!window.google?.maps?.Geocoder) {
      throw new Error("Google Maps API not fully loaded");
    }

    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat: latitude, lng: longitude };

    try {
      const response = await geocoder.geocode({ location: latlng });

      if (!response || !Array.isArray(response.results)) {
        console.error("Reverse geocoding failed. Response:", response);
        throw new Error("Failed to fetch address details");
      }

      const { results } = response;

      if (results?.[0]) {
        const { address_components, formatted_address } = results[0];
        const { city, state, country, area, postalCode } =
          extractLocationDetails(address_components);

        return {
          city: city || "Unknown City",
          state: state || "Unknown Region",
          latitude,
          longitude,
          country: country || "Unknown Country",
          area: area || postalCode || "",
          formattedAddress: formatted_address || "",
        };
      } else {
        throw new Error("No results found for this location");
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      throw error;
    }
  }, []);

  // Update location state and call callback
  const updateLocation = (newLocation) => {
    setCurrentLocation(newLocation);
    setShowPicker(false);
    if (onLocationSelected) {
      onLocationSelected(newLocation);
    }
    toast.success("Location updated successfully!");
  };

  // Use current location
  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser.");
      return;
    }

    setIsLoading(true);

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
        });
      });

      const { latitude, longitude } = position.coords;
      const locationDetails = await getAddressFromCoordinates(
        latitude,
        longitude
      );
      updateLocation(locationDetails);
    } catch (error) {
      console.error("Geolocation error:", error);
      const errorMessages = {
        [error.PERMISSION_DENIED]:
          "Location access denied. Please enable location services.",
        [error.POSITION_UNAVAILABLE]: "Location information unavailable.",
        [error.TIMEOUT]: "Location request timed out.",
        1: "Location access denied. Please enable location services.",
        2: "Location information unavailable.",
        3: "Location request timed out.",
      };

      toast.error(
        errorMessages[error.code] || "Unable to retrieve your location."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize Google Maps API
  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsGoogleMapsLoaded(true);
      return;
    }

    const handleScriptLoad = () => {
      setIsGoogleMapsLoaded(true);
    };

    const handleScriptError = () => {
      toast.error("Failed to load Google Maps API.");
      setIsGoogleMapsLoaded(false);
    };

    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com/maps/api/js"]`
    );

    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad);
      existingScript.addEventListener("error", handleScriptError);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${Maps_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.id = "google-maps-script-location-selector";
    script.addEventListener("load", handleScriptLoad);
    script.addEventListener("error", handleScriptError);
    document.head.appendChild(script);
    mapsScriptRef.current = script;

    return () => {
      if (mapsScriptRef.current) {
        mapsScriptRef.current.removeEventListener("load", handleScriptLoad);
        mapsScriptRef.current.removeEventListener("error", handleScriptError);
        document.head.removeChild(mapsScriptRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!initialLocation.city) {
      handleUseCurrentLocation();
    }
    // eslint-disable-next-line
  }, []);

  const PlacesAutocomplete = () => {
    const {
      ready,
      value,
      suggestions: { status, data },
      setValue,
      clearSuggestions,
    } = usePlacesAutocomplete({
      requestOptions: {
        types: ["geocode", "establishment"],
      },
      debounce: 300,
    });

    const containerRef = useRef(null);
    const [dropdownPos, setDropdownPos] = useState({
      top: 0,
      left: 0,
      width: 0,
    });

    useEffect(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    }, [value]);

    const handleSelect = async (address) => {
      setValue(address, false);
      clearSuggestions();

      try {
        const results = await getGeocode({ address });
        const { lat, lng } = await getLatLng(results[0]);
        const { address_components, formatted_address } = results[0];
        const { city, state, country, area } =
          extractLocationDetails(address_components);

        const newLocation = {
          city: city || address.split(",")[0].trim(),
          state,
          latitude: lat,
          longitude: lng,
          country,
          area: area || "",
          formattedAddress: formatted_address || address,
        };

        updateLocation(newLocation);
      } catch (error) {
        console.error("Error selecting address:", error);
        toast.error("Failed to process selected location. Please try again.");
      }
    };

    return (
      <>
        <div className="relative" ref={containerRef}>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!ready}
            placeholder="Enter location (e.g., New York, USA)"
            className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 mb-1"
          />
        </div>

        {status === "OK" &&
          createPortal(
            <div
              style={{
                position: "absolute",
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: dropdownPos.width,
                zIndex: 1500, // Higher than MUI Dialog (default ~1300)
                backgroundColor: "white",
                border: "1px solid #ccc",
                borderRadius: "6px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              {data.map(({ place_id, description }) => (
                <div
                  key={place_id}
                  onClick={() => handleSelect(description)}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                >
                  {description}
                </div>
              ))}
            </div>,
            document.body
          )}
      </>
    );
  };

  // Display component
  const DisplayComponent = () => (
    <div
      className={`flex items-center text-gray-700 hover:text-black text-sm font-medium cursor-pointer ${displayClassName}`}
      onClick={() => !displayOnly && setShowPicker(true)}
    >
      <FaMapMarkerAlt className="h-5 w-5 text-gray-500 mr-2" />

      {isLoading ? (
        <span className="animate-pulse">Loading location...</span>
      ) : !currentLocation?.city ? (
        <span>Select Location</span>
      ) : (
        <div className="flex flex-col">
          <div className="font-semibold leading-none md:text-sm text-xs">
            {currentLocation.area || "Selected Location"}
          </div>
          <div className="md:text-xs text-[10px] text-gray-500 leading-none">
            {[currentLocation.city, currentLocation.state]
              .filter(Boolean)
              .join(", ")}
          </div>
        </div>
      )}
    </div>
  );

  const PickerComponent = () => (
    <Dialog
      open={showPicker}
      onClose={() => setShowPicker(false)}
      maxWidth="xs"
      className="h-full"
      fullWidth
    >
      <div className="relative bg-white p-6 rounded-lg w-full md:p-8">
        <button
          onClick={() => setShowPicker(false)}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100"
          disabled={isLoading}
        >
          <FaTimes size={20} />
        </button>

        <h2 className="text-xl font-bold mb-4">Select Your Location</h2>

        <button
          onClick={handleUseCurrentLocation}
          disabled={!isGoogleMapsLoaded || isLoading}
          className={`w-full py-2 px-4 rounded-md mb-4 flex items-center justify-center ${
            !isGoogleMapsLoaded || isLoading
              ? "bg-blue-300 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          {isLoading ? (
            <span className="animate-pulse">Detecting location...</span>
          ) : (
            <>
              <FaMapMarkerAlt className="inline-block mr-2" />
              {isGoogleMapsLoaded ? "Use Current Location" : "Loading maps..."}
            </>
          )}
        </button>

        <div className="text-center text-gray-500 mb-4 font-semibold">
          - OR -
        </div>

        {isGoogleMapsLoaded ? (
          <PlacesAutocomplete />
        ) : (
          <div className="text-center py-4">Loading location services...</div>
        )}
      </div>
    </Dialog>
  );

  return (
    <>
      <DisplayComponent />
      {showPicker && <PickerComponent />}
    </>
  );
};

export default LocationSelector;
